/**
 * confirmGlobal — PHASE 11 B1 (I-02) : service de confirmation IMPÉRATIF global.
 *
 * Objectif : éradiquer `window.confirm()` (boîte système navigateur, rupture
 * premium totale) sans devoir monter un hook dans chaque composant.
 *
 * Usage (n'importe où, y compris hors composant) :
 *   import { confirmPremium } from '../lib/confirmGlobal';
 *   const ok = await confirmPremium({
 *     title: 'Supprimer ce document ?',
 *     body: 'Cette action est définitive.',
 *     confirmLabel: 'Supprimer',
 *     tone: 'danger',            // neutral | warning | danger
 *   });
 *   if (!ok) return;
 *
 * `<GlobalConfirmHost />` est monté UNE FOIS dans App.js et rend la modale
 * canonique premium (ConfirmDialog) pour toutes les demandes.
 */
import { useEffect, useState } from 'react';
import { ConfirmDialog } from '../components/premium/ConfirmDialog';

let _listener = null;

export function confirmPremium(options = {}) {
  // Repli défensif : si l'hôte n'est pas monté (tests unitaires, très early
  // render), on ne bloque pas l'action — confirmation native en dernier recours.
  if (!_listener) {
    // eslint-disable-next-line no-alert
    return Promise.resolve(window.confirm(options.title || 'Confirmer ?'));
  }
  return new Promise((resolve) => {
    _listener({ options, resolve });
  });
}

export function GlobalConfirmHost() {
  const [request, setRequest] = useState(null);

  useEffect(() => {
    _listener = (req) => setRequest(req);
    return () => { _listener = null; };
  }, []);

  if (!request) return null;
  const { options, resolve } = request;
  const close = (result) => { setRequest(null); resolve(result); };

  return (
    <ConfirmDialog
      open
      title={options.title || 'Confirmer ?'}
      body={options.body}
      confirmLabel={options.confirmLabel || 'Confirmer'}
      cancelLabel={options.cancelLabel || 'Annuler'}
      tone={options.tone || 'neutral'}
      overline={options.overline}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
      testid="global-confirm-dialog"
    />
  );
}
